import moment from "moment";
import { Platform } from "react-native";

export interface PayloadType {
  [props: string]: string | any;
}

export const getQueryParamsString = (payload: PayloadType, baseUrl: string) => {
  const params = Object.keys(payload);
  const paramsLength = params.length;
  let completeUrl = "?";

  if (!paramsLength) return baseUrl;

  params.forEach((param, index) => (completeUrl += Array.isArray(payload[param]) ?
    getQueryParamsWithSameKeyFromArray(param, payload[param]) :
    `${param}=${payload[param]}${index >= paramsLength - 1 ? "" : "&"}`));

  return `${baseUrl}${completeUrl}`;
};

export const parseToCurrency = (number: number, decimal = 0) => Number(number)
  .toFixed(decimal)
  .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");

export const getQueryParamsWithSameKeyFromArray = (key: string, array: Array<string>) => {
  let temp = "";

  array.forEach((elem, index) => (temp += `${key}=${elem}${index >= array.length ? "" : "&"}`));

  return temp;
};

export const getQueryQuoteFilters = (payload: PayloadType, baseUrl: string) => {
  const {
    page,
    equipment_type,
    comodity,
    customer,
    destination,
    origin,
    pick_up_date,
    drop_off_date,
    distance_from,
    distance,
    sort_by,
    load_id,
    order_by,
  } = payload;

  let url = `${baseUrl}?limit=20&page=${page}`;

  if (equipment_type.length) {
    equipment_type.forEach((e: any) => {
      url += `&equipment_type=${e?.equipment_code}`;
    });
  }

  if (Object.keys(comodity).length) {
    url += `&comodity=${comodity?.commodity_id}`;
  }

  if (Object.keys(customer).length) {
    url += `&customer=${customer?.customer_id}`;
  }

  if (destination.length) {
    destination.forEach((e: any) => {
      url += `&destination=${e?.location_id}`;
    });
  }

  if (origin.length) {
    origin.forEach((e: any) => {
      url += `&origin=${e?.location_id}`;
    });
  }

  if (pick_up_date) {
    url += `&pick_up_date=${moment(pick_up_date).format("YYYY-MM-DD")}`;
  }

  if (drop_off_date) {
    url += `&drop_off_date=${moment(drop_off_date).format("YYYY-MM-DD")}`;
  }

  if (Object.keys(distance_from).length) {
    url += `&distance_from=${distance_from?.id}`;
  }

  if (Object.keys(distance).length) {
    url += `&distance=${distance?.distance}`;
  }

  if (Object.keys(sort_by).length) {
    url += `&sort_by=${sort_by?.id}`;
  }

  if (load_id) {
    url += `&load_id=${load_id}`;
  }

  if (order_by) {
    url += `&order_by=${order_by}`;
  }

  return url;
};

export const validateFilters = ({
  equipment_type,
  comodity,
  customer,
  destination,
  origin,
  pick_up_date,
  drop_off_date,
  distance_from,
  distance,
}: PayloadType): { isInvalid: boolean; message: string } => {
  let isInvalid = false;
  let message = "";

  if (
    !equipment_type.length
    && !Object.keys(comodity).length
    && !Object.keys(customer).length
    && !destination.length
    && !origin.length
    && !pick_up_date
    && !drop_off_date
    && !Object.keys(distance_from).length
    && !Object.keys(distance).length
  ) {
    isInvalid = true;
    message = "Fill in the search fields.";
  }

  if (distance_from?.id && !distance?.distance) {
    isInvalid = true;
    message = "Unable to filter with radius from origin if distance is not selected";
  }

  if (distance_from?.id === "ORIGIN" && distance?.distance && !origin.length) {
    isInvalid = true;
    message = "Unable to filter with radius from origin if an origin is not selected";
  }

  if (distance_from?.id === "DESTINATION" && distance?.distance && !destination.length) {
    isInvalid = true;
    message = "Unable to filter with radius from destination if an destination is not selected";
  }

  if (distance_from?.id === "BOTH" && distance?.distance && (!origin.length || !destination.length)) {
    isInvalid = true;
    message = "Unable to filter with radius from both if origin and destination are not selected";
  }

  return { isInvalid, message };
};

export const getLocationFormat = (location: any): string =>
  (location?.city ?
    `${capitalizedLocation(location?.city)}, ${location?.state_code || location?.state_id}` :
    location?.state_code || location?.state_id ?
      `${capitalizedLocation(location?.state)}(${location?.state_code || location?.state_id})` :
      "- -");

export const getTextCapitalized = (text: string) => {
  let temp = "";

  if (text) {
    text.split(" ").forEach((item) => {
      temp += ` ${item.charAt(0).toUpperCase()}${item.toLowerCase().slice(1)}`;
    });
  }

  return temp.length ? temp.slice(1, temp.length) : temp;
};

export const getLocationText = (location?: any, showCountry?: boolean) => {
  let locationText = "- -";

  if (location?.city) {
    locationText = getTextCapitalized(location.city)
      .concat(`, ${location.state_id}${showCountry ? ` - ${location.country}` : ""}`)
      .trim();
  } else if (location?.state) {
    locationText = getTextCapitalized(location.state)
      .concat(` (${location.state_id})${showCountry ? ` - ${location.country}` : ""}`)
      .trim();
  }

  return locationText;
};
export const setFormatDate = (date: string, formatDate = "MM/DD/YYYY") => (date ? moment(date).format(formatDate) : "");

export const setFormatDateWithoutOffset = (date: any, formatDate = "MM/DD/YYYY") => {
  const cleanDate = date ? (date.includes("T") ? date.split("T")[0] : date) : "";

  return date ? moment(cleanDate).format(formatDate) : "";
};

export const setFormatTime = (date: string, formatDate = "HH:mm") => (date ? moment(date).format(formatDate) : "");

export const setToDateFormat = (date: string, format = "YYYY-MM-DD hh:mm:ss") => moment(date, format).toDate();

export const setToISODateFormat = (date: string) => (date ? moment(date).toISOString() : "");

export const dateIsBefore = (date1: string, date2: string) => (date1 && date2 ? moment(date1).isBefore(date2) : false);

export const parseTimeForTimePicker = (date: string, time: any) => new Date(
  ` ${typeof date === "string" ? date.split("T")[0] : setFormatDate(date, "MM/DD/YYYY")} ${
    typeof time === "string" ? time : time.format("HH:mm")
  }`,
);

export const parseNumberWithThousandSeparator = (price = "0"): string => parseFloat(price).toLocaleString("en");

export const setWeight = (weight: string): string => parseNumberWithThousandSeparator(weight);

export const capitalizedLocation = (locationText: string) => {
  let temp = "";

  if (locationText) {
    locationText?.split(" ").forEach((item) => {
      temp += ` ${item.charAt(0).toUpperCase()}${item.toLowerCase().slice(1)}`;
    });
  }

  return temp;
};

export const capitalize = (text: string) => `${text.charAt(0).toUpperCase()}${text.toLowerCase().slice(1)}`;

export const toArrayBuffer = (buf: Buffer) => {
  const ab = new ArrayBuffer(buf.length);
  const view = new Uint8Array(ab);

  for (let i = 0; i < buf.length; ++i) {
    view[i] = buf[i];
  }

  return ab;
};

export const renderEl = (rootEl: string, el: string) => {
  const root = document.getElementById(rootEl);
  const modalRoot = document.createElement("div");
  const currentRoot = document.getElementById(el);

  modalRoot.id = el;

  currentRoot && currentRoot.remove();

  root && root.insertAdjacentElement("afterend", modalRoot);

  return modalRoot;
};

export const formatSaveStorage = (data: any) => {
  const expiration = Date.now() + data.expires_in * 1000;
  const refresh_exp = Date.now() + (data.refresh_expires_in ? data.refresh_expires_in : 604800) * 1000;

  return {
    ...data,
    expires_in         : expiration,
    refresh_expires_in : refresh_exp,
  };
};

export const customLineHeight = (value: number) => (Platform.OS === "android" ? value : `${value}px`);

export const toFixed = (number?: number | null, dec = 2) => (number ? number.toFixed(dec) : 0);

export const removeEmptyValuesFromObject = (obj: any) => {
  for (const propName in obj) {
    if (!obj[propName]) {
      delete obj[propName];
    }
  }

  return obj;
};

export const parsedNumberScale = (value: number, desiredScale: number) => {
  if (desiredScale !== 0) return Math.ceil(value / desiredScale);

  return 0;
};
